import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { MailPlus, Trash2 } from 'lucide-react'
import type { RolNivel } from '@/types'

interface Invitation {
  id: string
  email: string
  rol_nivel: RolNivel
  created_at: string
  tour_ids: string[]
}

interface Props {
  orgId: string
  // When provided, only shows invitations whose tour_ids includes this tour.
  tourId?: string
}

export function PendingInvitationsList({ orgId, tourId }: Props) {
  const queryClient = useQueryClient()

  const { data: invitations = [], isLoading } = useQuery({
    queryKey: ['org-invitations', orgId, tourId ?? null],
    queryFn: async (): Promise<Invitation[]> => {
      let query = supabase
        .from('org_invitations')
        .select('id, email, rol_nivel, created_at, tour_ids')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false })
      if (tourId) {
        // Postgres array contains operator — row kept iff its tour_ids @> [tourId]
        query = query.contains('tour_ids', [tourId])
      }
      const { data, error } = await query
      if (error) throw error
      return (data ?? []) as Invitation[]
    },
  })

  const cancel = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('org_invitations').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Invitation canceled')
      queryClient.invalidateQueries({ queryKey: ['org-invitations', orgId, tourId ?? null] })
      queryClient.invalidateQueries({ queryKey: ['org-invitations', orgId, null] })
    },
    onError: () => toast.error('Failed to cancel invitation'),
  })

  if (isLoading || invitations.length === 0) return null

  return (
    <div className="space-y-1">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium pt-1">
        Pending
      </p>
      <ul className="space-y-1">
        {invitations.map(inv => (
          <li key={inv.id} className="flex items-center gap-2 text-sm">
            <MailPlus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="flex-1 min-w-0 truncate text-muted-foreground">{inv.email}</span>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {roleLabel(inv.rol_nivel)}
            </span>
            <button
              onClick={() => {
                if (confirm(`Cancel invitation for ${inv.email}?`)) cancel.mutate(inv.id)
              }}
              className="p-1 text-muted-foreground hover:text-destructive"
              aria-label="Cancel invitation"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

function roleLabel(rol: RolNivel) {
  if (rol === 'A') return 'Manager'
  if (rol === 'B') return 'Artist'
  return 'Crew'
}
